import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { blogData } from "../utils/blogData";

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[\s+]+/g, "-")
    .replace(/[^a-z0-9\-]/g, "")
    .replace(/\-+/g, "-")
    .replace(/^\-+|\-+$/g, "");
}

const BlogDetail = () => {
  const { slug } = useParams();
  const blog = useMemo(
    () => blogData.find((item) => item.slug === slug),
    [slug]
  );

  const [processedHtml, setProcessedHtml] = useState("");
  const [toc, setToc] = useState([]); 

  useEffect(() => {
    if (!blog?.content) return;

    // Build a DOM we can modify
    const container = document.createElement("div");
    container.innerHTML = blog.content;

    // ✅ Ensure there is only ONE H1 on the page:
    // downgrade any H1s inside the article body to H2 for SEO
    const h1s = Array.from(container.querySelectorAll("h1"));
    h1s.forEach((h1) => {
      const h2 = document.createElement("h2");
      // copy attributes
      for (const attr of h1.getAttributeNames()) {
        h2.setAttribute(attr, h1.getAttribute(attr) || "");
      }
      // preserve content
      h2.innerHTML = h1.innerHTML;
      h1.replaceWith(h2);
    });

    // Collect H2s for Table of Contents and add IDs
    const seen = new Map();
    const headings = Array.from(container.querySelectorAll("h2"));
    const tocItems = headings.map((h2) => {
      const text = h2.textContent?.trim() || "section";
      let id = slugify(text) || "section";

      // Ensure unique ids if duplicate headings exist
      const count = seen.get(id) || 0;
      seen.set(id, count + 1);
      if (count > 0) id = `${id}-${count + 1}`;

      h2.setAttribute("id", id);
      h2.classList.add("scroll-mt-24"); // anchor offset for sticky headers
      return { id, text };
    });

    setToc(tocItems);
    setProcessedHtml(container.innerHTML);
  }, [blog]);

  if (!blog) {
    return (
      <div className="text-center py-10 text-red-600">Blog not found</div>
    );
  }

  const url = `https://buildpro.ogcs.co.in/blog/${blog.slug}`;
  const description =
    blog.summary || blog.excerpt || "Read this article on the BuildPro OGCS blog.";

  const handleJump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="px-6 py-10 max-w-7xl mx-auto">
      {/* Page-level SEO */}
      <Helmet>
        <title>{`${blog.title} | BuildPro OGCS Blog`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />

        {/* Open Graph / Twitter */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${blog.title} | BuildPro OGCS Blog`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        {blog.image && <meta property="og:image" content={blog.image} />}
        <meta name="twitter:card" content="summary_large_image" />
        {blog.image && <meta name="twitter:image" content={blog.image} />}

        {/* Article meta (optional if available) */}
        {blog.publishedAt && (
          <meta property="article:published_time" content={blog.publishedAt} />
        )}
        {blog.updatedAt && (
          <meta property="article:modified_time" content={blog.updatedAt} />
        )}

        {/* Schema.org: BlogPosting */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": blog.title,
          "image": blog.image ? [blog.image] : undefined,
          "datePublished": blog.publishedAt || undefined,
          "dateModified": blog.updatedAt || blog.publishedAt || undefined,
          "author": blog.authorName ? { "@type": "Person", "name": blog.authorName } : undefined,
          "publisher": {
            "@type": "Organization",
            "name": "BuildPro OGCS",
            "logo": { "@type": "ImageObject", "url": "https://buildpro.ogcs.co.in/logo.png" }
          },
          "description": description,
          "mainEntityOfPage": { "@type": "WebPage", "@id": url }
        })}</script>
      </Helmet>

      {/* ✅ Single H1 = Post Title (kept exactly as your UI text) */}
      <h1 className="text-3xl font-bold mb-6 text-gray-800">{blog.title}</h1>

      {blog.image && (
        <img
          src={blog.image}
          alt={blog.title}
          loading="lazy"
          className="mb-8 w-full rounded-md shadow-lg"
        />
      )}

      {/* 12-col layout: left = contents, right = article */}
      <div className="grid grid-cols-12 gap-8">
        {/* Left: Table of Contents */}
        <aside className="col-span-12 lg:col-span-3" aria-label="Table of contents">
          <div className="lg:sticky lg:top-24 bg-white/80 backdrop-blur border border-gray-200 rounded-xl shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              Contents
            </h2>
            {toc.length ? (
              <ul className="space-y-2">
                {toc.map(({ id, text }) => (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => handleJump(id)}
                      className="text-left text-sm text-gray-700 hover:text-blue-600 hover:underline"
                    >
                      {text}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No sections found.</p>
            )}
          </div>
        </aside>

        {/* Right: Article */}
        <article className="col-span-12 lg:col-span-9">
          <div
            className="prose lg:prose-xl max-w-none"
            // processedHtml has: downgraded H1s to H2 + IDs on all H2s
            dangerouslySetInnerHTML={{ __html: processedHtml }}
          />
        </article>
      </div>
    </main>
  );
};

export default BlogDetail;
